import {Injectable} from '@angular/core';
import {combineLatest, Observable} from 'rxjs';
import {CertificationPerUnit, UnitOfLearningOutcomesStudentEvaluation} from '@model/certification-per-unit';
import {select, Store} from '@ngrx/store';
import {getCertificationsPerUnitSelected, getStudentsRegisteredForCertificationPerUnit, StoreState} from '@redux/reducers';
import {CertificationPerUnitService} from '@services/certifications-per-unit.service';
import {
  CertificationPerUnitForStudentLoaded,
  CertificationsPerUnitLoaded,
  CertificationsPerUnitSelectedForSchool,
  CertificationsPerUnitUnselectedForSchool,
  EvaluationForOperationalIndicatorAdded,
  RegisterStudentForCertificationPerUnit,
  StudentsRegisteredForCertificationPerUnitLoaded,
  UnregisterStudentForCertificationPerUnit
} from '@redux/actions/certifications-per-unit.action';
import {SharedFacadeService} from '@shared/shared-facade.service';
import {Class} from '@model/class';
import {Page} from '@model/page';
import {StudentView} from '@model/student-view';
import {School} from '@model/school';
import {map, switchMap, take} from 'rxjs/operators';
import {StudentRegisteredToCPU, StudentViewWithRegisterToCPU} from '@model/student-view-with-register-to-cpu';
import {OperationIndicatorResult} from '@model/operation-indicator-result';
import {documentGenerationRequestStarted, documentGenerationRequestStopped} from '@redux/actions/ui.action';
import {MatSnackBar} from '@angular/material/snack-bar';

@Injectable()
export class CertificationsPerUnitFacadeService {
  certificationsPerUnit$: Observable<CertificationPerUnit[]>;
  allCertificationsPerUnit$: Observable<CertificationPerUnit[]>;
  studentsRegisteredToCPU$: Observable<StudentRegisteredToCPU[]>;
  classes$: Observable<Class[]>;
  studentSearchResult$: Observable<Page<StudentView>>;
  school$: Observable<School>;
  studentsWithRegisterToCPU$: Observable<StudentViewWithRegisterToCPU[]>;
  certificationPerUnitForStudent$: Observable<CertificationPerUnit>;
  documentGenerationRequestOngoing$: Observable<boolean>;
  isMobile$: Observable<boolean>;

  constructor(
    private _store: Store<StoreState>,
    private _sharedFacade: SharedFacadeService,
    private _certificationPerUnitService: CertificationPerUnitService,
    private _snackBar: MatSnackBar,
  ) {
    this.allCertificationsPerUnit$ = this._sharedFacade.certificationsPerUnit$
    this.certificationsPerUnit$ = this._store.pipe(select(getCertificationsPerUnitSelected));
    this.studentsRegisteredToCPU$ = this._store.pipe(select(getStudentsRegisteredForCertificationPerUnit));
    this.classes$ = this._sharedFacade.classes$;
    this.studentSearchResult$ = this._sharedFacade.studentSearchResult$;
    this.school$ = this._sharedFacade.school$;
    this.studentsWithRegisterToCPU$ = combineLatest([
      this.studentSearchResult$, this.studentsRegisteredToCPU$
    ]).pipe(
      map(([studentSearchResult, listStudentsForCPU]) => {
        return studentSearchResult.content.map(student => ({
          ...student,
          registerToCPU: listStudentsForCPU.map( s => s.studentId).includes(student.id)
        }));
      })
    );
    this.certificationPerUnitForStudent$ = this._sharedFacade.certificationPerUnitForStudent$;
    this.isMobile$ = this._sharedFacade.isMobile$;
    this.documentGenerationRequestOngoing$ = this._sharedFacade.documentGenerationRequestOngoing$;
  }

  loadCertificationsPerUnit() {
    this.school$.pipe(
      take(1),
      switchMap(school => this._certificationPerUnitService.loadCertificationsPerUnit(school.id))
    ).subscribe(certificationsPerUnit => this._store.dispatch(
      CertificationsPerUnitLoaded({ certificationsPerUnit })
    ));
  }

  updateSchoolSelectionForCertificationPerUnit(certificationPerUnitId: string, studentEvaluationsConsultationEnabled: boolean) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._certificationPerUnitService.putSchoolSelectionForCertificationPerUnit(school.id, certificationPerUnitId, studentEvaluationsConsultationEnabled))
    ).subscribe(() => {
      this._store.dispatch(CertificationsPerUnitSelectedForSchool({certificationPerUnitId, studentEvaluationsConsultationEnabled}));
    });
  }

  removeSchoolSelectionForCertificationPerUnit(certificationPerUnitId: string) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._certificationPerUnitService.deleteSchoolSelectionForCertificationPerUnit(school.id, certificationPerUnitId))
    ).subscribe(() => {
      this._store.dispatch(CertificationsPerUnitUnselectedForSchool({certificationPerUnitId}));
    });
  }

  loadStudentsByClass(classId: number) {
    this._sharedFacade.loadStudentsByClass(classId);
  }

  loadStudentsRegisteredForCertificationPerUnit(certificationPerUnitId: string) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._certificationPerUnitService.loadStudentsRegisteredForCertificationPerUnit(school.id, certificationPerUnitId))
    ).subscribe((listStudentsForCPU) =>
      this._store.dispatch(StudentsRegisteredForCertificationPerUnitLoaded({studentsRegisteredForCertificationPerUnit: listStudentsForCPU}))
    );
  }

  registerStudentForCertificationPerUnit(certificationPerUnitId: string, studentId: number) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._certificationPerUnitService.registerStudentForCertificationPerUnit(school.id, certificationPerUnitId, studentId))
    ).subscribe(() => this._store.dispatch(RegisterStudentForCertificationPerUnit({student: {studentId}})));
  }

  unregisterStudentForCertificationPerUnit(certificationPerUnitId: string, studentId: number) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._certificationPerUnitService.unregisterStudentForCertificationPerUnit(school.id, certificationPerUnitId, studentId))
    ).subscribe(() => this._store.dispatch(UnregisterStudentForCertificationPerUnit({studentId})));
  }

  saveEvaluationForOperationalIndicator(operationIndicatorResult: OperationIndicatorResult) {
    this.school$.pipe(
      take(1),
      switchMap(school => {
        if (operationIndicatorResult.evaluation1Result || operationIndicatorResult.evaluation2Result) {
          return this._certificationPerUnitService.addEvaluationForOperationalIndicator(school.id, operationIndicatorResult);
        } else {
          return this._certificationPerUnitService.removeEvaluationForOperationalIndicator(
            school.id,
            operationIndicatorResult.certificationPerUnitId,
            operationIndicatorResult.operationalIndicatorId,
            operationIndicatorResult.studentId);
        }
      })
    ).subscribe(certificationPerUnit => this._store.dispatch(EvaluationForOperationalIndicatorAdded({certificationPerUnit})));
  }

  loadCertificationPerUnitWithResultsForStudent(certificationPerUnitId: string, studentId: number) {
    this._sharedFacade.loadCertificationPerUnitWithResultsForStudent(certificationPerUnitId, studentId);
  }

  saveStudentEvaluationForUnitOfLearningOutcome(certificationPerUnitId: string, studentId: number, unitOfLearningOutcomesId: string, studentEvaluation: UnitOfLearningOutcomesStudentEvaluation) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._certificationPerUnitService.saveStudentEvaluationForUnitOfLearningOutcome(school.id, certificationPerUnitId, studentId, unitOfLearningOutcomesId, studentEvaluation))
    ).subscribe(certificationPerUnit => {
      this._store.dispatch(CertificationPerUnitForStudentLoaded({certificationPerUnit}));
      this._snackBar.open('Évaluation enregistrée', null, {duration: 2000})
    });
  }

  createCertificationPerUnitPdfGenerationRequest(certificationPerUnitId: string, studentId: number) {
    this._store.dispatch(documentGenerationRequestStarted());
    this.school$.pipe(
      take(1),
      switchMap(school => this._certificationPerUnitService.createCertificationPerUnitPdfGenerationRequest(school.id, certificationPerUnitId, studentId))
    ).subscribe(
      (locationUrl) => this._sharedFacade.startPollingDocument(locationUrl, documentGenerationRequestStopped()),
      (err) => {
        this._store.dispatch(documentGenerationRequestStopped());
        throw err;
      });
  }
}
